import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import '../../DashboardHome/ai-quality.css';
import './aiQuality.css';

const QI = {
    check: (
        <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
            <path d="M3 7.5l2.5 2.5L11 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    spark: (
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
            <path d="M8 2l1.3 3.4L13 6.6 9.7 8 8 11.5 6.3 8 3 6.6l3.4-1.2L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
    ),
    refresh: (
        <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
            <path d="M11.5 3.5a5 5 0 10.8 4.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M11.5 1.5v2.5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    caret: (
        <svg viewBox="0 0 12 12" width="11" height="11" fill="none">
            <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    arrow: (
        <svg viewBox="0 0 12 12" width="11" height="11" fill="none">
            <path d="M2 6h7M6 3l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
};

const RISK = {
    terminology: { label: 'Terminology', sev: 'high' },
    tags: { label: 'Tag mismatch', sev: 'high' },
    untranslated: { label: 'Untranslated', sev: 'high' },
    length: { label: 'Length', sev: 'med' },
    fluency: { label: 'Fluency', sev: 'med' },
    number: { label: 'Number drift', sev: 'med' },
    formality: { label: 'Formality', sev: 'med' },
};

const bucketOf = (s, th) => {
    if ((s.risks || []).some((r) => RISK[r]?.sev === 'high')) return 'risk';
    if (s.score >= th && (s.risks || []).length === 0) return 'safe';
    return 'review';
};

const ScoreGauge = ({ score }) => {
    const band = score >= 90 ? 'hi' : score >= 80 ? 'mid' : 'lo';
    return (
        <div className={`aiq-gauge ${band}`}>
            <div className="bar"><span style={{ width: `${score}%` }} /></div>
            <span className="num">{score}</span>
        </div>
    );
};

const SegmentRow = ({ s, bucket, expanded, onToggle, onApprove, onOpen, onSave, done }) => {
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState(s.tgt || '');
    const [saving, setSaving] = React.useState(false);
    const [saveErr, setSaveErr] = React.useState(null);

    // Re-sync the draft if the underlying segment text changes (e.g. after a
    // re-run) while we're not mid-edit.
    React.useEffect(() => {
        if (!editing) setDraft(s.tgt || '');
    }, [s.tgt, editing]);

    const startEdit = () => { setDraft(s.tgt || ''); setSaveErr(null); setEditing(true); };
    const cancelEdit = () => { setDraft(s.tgt || ''); setSaveErr(null); setEditing(false); };
    const save = async () => {
        if (draft === (s.tgt || '')) { setEditing(false); return; }
        setSaving(true);
        setSaveErr(null);
        try {
            await onSave(draft);
            setEditing(false);
        } catch (err) {
            setSaveErr(err?.response?.data?.msg || 'Save failed. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`aiq-row ${bucket} ${done ? 'done' : ''}`}>
            <div className="aiq-row-main" onClick={onToggle} role="button" tabIndex={0}>
                <span className="aiq-n">#{s.n}</span>
                {s.occurrences > 1 && (
                    <span className="aiq-occ" title={`This source appears ${s.occurrences} times in the project — editing updates every identical copy`}>×{s.occurrences}</span>
                )}
                <div className="aiq-texts">
                    <span className="src">{s.src}</span>
                    <span className="tgt">
                        {s.tgt ? (
                            <>
                                <span className="ai-tag">{s._edited ? 'EDIT' : 'AI'}</span>{s.tgt}
                            </>
                        ) : (
                            <span className="empty">— no draft —</span>
                        )}
                    </span>
                </div>
                <ScoreGauge score={s.score} />
                <div className="aiq-risks">
                    {(s.risks || []).length === 0 ? (
                        <span className="aiq-risk clean">{QI.check} Clean</span>
                    ) : (
                        s.risks.map((r) => (
                            <span key={r} className={`aiq-risk ${RISK[r]?.sev || 'med'}`}>{RISK[r]?.label || r}</span>
                        ))
                    )}
                </div>
                <span className={`aiq-caret ${expanded ? 'open' : ''}`}>{QI.caret}</span>
            </div>
            {expanded && (
                <div className="aiq-why">
                    <div className="aiq-why-h">{QI.spark} Why this score</div>
                    <p>{s.why || (s.risks.length ? `Risks detected: ${s.risks.map((r) => RISK[r]?.label || r).join(', ')}.` : 'No risks detected; score reflects model confidence.')}</p>

                    {editing ? (
                        <div className="aiq-edit">
                            <div className="aiq-edit-src">{s.src}</div>
                            <textarea
                                className="aiq-edit-input"
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                rows={3}
                                autoFocus
                                disabled={saving}
                                onKeyDown={(e) => {
                                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save();
                                    if (e.key === 'Escape') cancelEdit();
                                }}
                            />
                            {saveErr && <div className="aiq-edit-err">{saveErr}</div>}
                            <div className="aiq-why-actions">
                                <button type="button" className="set-btn sm primary" onClick={save} disabled={saving}>
                                    {saving ? 'Saving…' : 'Save translation'}
                                </button>
                                <button type="button" className="set-btn sm" onClick={cancelEdit} disabled={saving}>
                                    Cancel
                                </button>
                                <span className="aiq-edit-hint">
                                    ⌘/Ctrl+Enter to save · re-run analysis to re-score
                                    {s.occurrences > 1 ? ' · updates every identical copy' : ''}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="aiq-why-actions">
                            <button type="button" className="set-btn sm primary" onClick={onApprove}>
                                {QI.check} Approve
                            </button>
                            <button type="button" className="set-btn sm" onClick={startEdit}>
                                Edit translation
                            </button>
                            <button type="button" className="set-btn sm" onClick={onOpen}>
                                Open in editor {QI.arrow}
                            </button>
                            {s._edited && <span className="aiq-edit-hint">Edited — re-run analysis to refresh the score.</span>}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const AIQuality = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [projects, setProjects] = useState([]);
    const [searchParams] = useSearchParams();
    const [scopeProjectId, setScopeProjectId] = useState(() => searchParams.get('projectId') || '');
    const [scopeGroupId, setScopeGroupId] = useState(() => searchParams.get('projectGroupId') || '');
    const [data, setData] = useState({ stats: {}, segments: [] });
    const [threshold, setThreshold] = useState(88);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [approving, setApproving] = useState(false);
    const [approveProgress, setApproveProgress] = useState(0);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [approved, setApproved] = useState({});
    const [reloadKey, setReloadKey] = useState(0);

    const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const fetchData = React.useCallback(() => {
        if (!token) return;
        setLoading(true);
        setError(null);
        const scopeQuery = scopeProjectId
            ? `&projectId=${scopeProjectId}`
            : scopeGroupId
                ? `&projectGroupId=${scopeGroupId}`
                : '';
        const url = `/api/dashboard/ai-quality?threshold=${threshold}${scopeQuery}`;
        axios.get(url, { headers: authHeader })
            .then((r) => {
                setData(r.data || { stats: {}, segments: [] });
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load AI quality.');
                    setLoading(false);
                }
            });
    }, [token, threshold, scopeProjectId, scopeGroupId, authHeader, dispatch, navigate]);

    const [progress, setProgress] = useState(0);
    const [segTotal, setSegTotal] = useState(0);
    const pollTimer = React.useRef(null);

    const stopPolling = () => {
        if (pollTimer.current) {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
        }
    };

    React.useEffect(() => () => stopPolling(), []);

    const pollStatus = (projectId) => {
        stopPolling();
        pollTimer.current = setInterval(async () => {
            try {
                const r = await axios.get(`/api/ai-quality/${projectId}/analyze/status`, { headers: authHeader });
                const { status, progress: pct, error: bgErr, total } = r.data || {};
                setProgress(pct || 0);
                if (total) setSegTotal(total);
                if (status === 'done') {
                    stopPolling();
                    setAnalyzing(false);
                    setProgress(100);
                    fetchData();
                } else if (status === 'failed') {
                    stopPolling();
                    setAnalyzing(false);
                    setError(bgErr || 'Analysis failed. Please try again.');
                }
            } catch (err) {
                console.warn('poll failed', err.message || err);
            }
        }, 2000);
    };

    const approvePollTimer = React.useRef(null);
    const stopApprovePolling = () => {
        if (approvePollTimer.current) {
            clearInterval(approvePollTimer.current);
            approvePollTimer.current = null;
        }
    };
    React.useEffect(() => () => stopApprovePolling(), []);

    const pollApproveStatus = (projectId) => {
        stopApprovePolling();
        approvePollTimer.current = setInterval(async () => {
            try {
                const r = await axios.get(`/api/ai-quality/${projectId}/approve-safe/status`, { headers: authHeader });
                const { status, progress: pct, error: bgErr } = r.data || {};
                setApproveProgress(pct || 0);
                if (status === 'done') {
                    stopApprovePolling();
                    setApproving(false);
                    setApproveProgress(100);
                    fetchData();
                } else if (status === 'failed') {
                    stopApprovePolling();
                    setApproving(false);
                    setError(bgErr ? `Auto-approve failed: ${bgErr}` : 'Auto-approve failed. Please try again.');
                }
            } catch (err) {
                console.warn('approve poll failed', err.message || err);
            }
        }, 2000);
    };

    const runAnalysis = async ({ force = false } = {}) => {
        // Resolve the set of project ids the analyze run should cover.
        //  - Specific language picked → that one project.
        //  - Group picked (multi-language source file) → every sibling.
        //  - Otherwise refetch only — running QE across the whole workspace is
        //    deliberately gated to avoid runaway cost.
        let targets = [];
        if (scopeProjectId) {
            targets = [scopeProjectId];
        } else if (selectedGroup) {
            targets = selectedGroup.siblings.map((s) => s._id);
        }
        if (!targets.length) {
            fetchData();
            return;
        }
        setAnalyzing(true);
        setError(null);
        setProgress(0);
        try {
            const results = await Promise.all(targets.map((pid) =>
                axios.post(
                    `/api/ai-quality/${pid}/analyze`,
                    { force },
                    { headers: authHeader },
                ).then((r) => ({ pid, status: r.data?.status }))
                    .catch((err) => ({ pid, error: err }))
            ));
            // Find a sibling that's running in the background to attach the
            // poller to — if every call returned 'done' synchronously we can
            // just refetch immediately. 409 (already in progress) is treated
            // as a running job we can attach to.
            const running = results.find((x) => !x.error && x.status !== 'done')
                || results.find((x) => x.error && x.error.response?.status === 409);
            const failed = results.find((x) => x.error && x.error.response?.status !== 409);
            if (failed) {
                console.error('AI Quality analyze failed', failed.error);
                setAnalyzing(false);
                setError('Re-run failed for at least one language. Please try again.');
                return;
            }
            if (running) {
                pollStatus(running.pid);
            } else {
                setAnalyzing(false);
                fetchData();
            }
        } catch (err) {
            console.error('AI Quality analyze failed', err);
            setAnalyzing(false);
            setError('Re-run failed. Please try again.');
        }
    };

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/projects?summary=true', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                const { createdProjects = [], sharedProjects = [] } = r.data || {};
                const unique = new Map([...createdProjects, ...sharedProjects].map((p) => [p._id, p]));
                const list = [...unique.values()];
                setProjects(list);
            })
            .catch(() => {});
    }, [token]);

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [fetchData, reloadKey]);

    // Hydrate prior approve / send-to-review decisions on scope change.
    useEffect(() => {
        if (!token) return;
        const params = scopeProjectId
            ? `?projectId=${scopeProjectId}`
            : scopeGroupId
                ? `?projectGroupId=${scopeGroupId}`
                : '';
        axios.get(`/api/ai-quality/decisions${params}`, { headers: authHeader })
            .then((r) => {
                const next = {};
                (r.data?.decisions || []).forEach((d) => {
                    if (d.decision === 'approved') {
                        next[`${d.projectId}-${d.segmentN}`] = true;
                    }
                });
                setApproved(next);
            })
            .catch(() => {});
    }, [token, scopeProjectId, scopeGroupId, authHeader]);

    // If a background analysis is already running for this project (e.g. the
    // user reloaded mid-run), attach to it and resume polling.
    useEffect(() => {
        if (!token || !scopeProjectId) return;
        axios.get(`/api/ai-quality/${scopeProjectId}/analyze/status`, { headers: authHeader })
            .then((r) => {
                if (r.data?.status === 'running') {
                    setAnalyzing(true);
                    setProgress(r.data.progress || 0);
                    pollStatus(scopeProjectId);
                }
            })
            .catch(() => {});
        // pollStatus is stable enough — capturing scopeProjectId is what matters.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, scopeProjectId, authHeader]);

    const persistDecision = (s, decision) => {
        axios.post('/api/ai-quality/decisions', {
            projectId: s.projectId,
            segmentN: s.n,
            decision,
        }, { headers: authHeader }).catch((err) => {
            console.warn('decision persist failed', err.message || err);
        });
    };

    const buckets = useMemo(() => {
        const safe = [];
        const review = [];
        const risk = [];
        (data.segments || []).forEach((s) => {
            if (approved[`${s.projectId}-${s.n}`]) return;
            const b = bucketOf(s, threshold);
            if (b === 'safe') safe.push(s);
            else if (b === 'review') review.push(s);
            else risk.push(s);
        });
        return { safe, review, risk };
    }, [data.segments, threshold, approved]);

    const queue = useMemo(
        () => [...buckets.risk, ...buckets.review].sort((a, b) => a.score - b.score),
        [buckets],
    );

    const stats = data.stats || {};
    const avgScore = stats.avgScore || 0;

    // True totals come from the server (computed over every segment); the
    // client `buckets` only cover the capped review-queue sample, so prefer
    // stats.* and fall back to buckets when stats aren't present yet.
    const safeCount = typeof stats.safeCount === 'number' ? stats.safeCount : buckets.safe.length;
    const riskCount = typeof stats.riskCount === 'number' ? stats.riskCount : buckets.risk.length;
    const reviewCount = typeof stats.reviewCount === 'number' ? stats.reviewCount : buckets.review.length;
    const triageTotal = safeCount + riskCount + reviewCount;
    const pct = (n) => (triageTotal ? (n / triageTotal) * 100 : 0);

    // Bulk-approve every safe segment server-side. The dashboard only ships the
    // worst 500 rows, so we can't approve safe segments from `buckets` — instead
    // fan out the approve-safe endpoint across the scoped project(s), same as
    // Re-run, then refetch to pull the updated counts.
    const autoApprove = async () => {
        const targets = scopeProjectId
            ? [scopeProjectId]
            : (selectedGroup ? selectedGroup.siblings.map((s) => s._id) : []);
        if (!targets.length) return;
        setApproving(true);
        setApproveProgress(0);
        setError(null);
        try {
            // Each target returns 202 ('running') and does the heavy work in the
            // background — mirrors Re-run. Attach the poller to one running
            // sibling (or a 409 already-in-progress job); if every call came back
            // 'done' synchronously (no safe segments), just refetch.
            const results = await Promise.all(targets.map((pid) =>
                axios.post(`/api/ai-quality/${pid}/approve-safe`, { threshold }, { headers: authHeader })
                    .then((r) => ({ pid, status: r.data?.status }))
                    .catch((err) => ({ pid, error: err }))));
            const running = results.find((x) => !x.error && x.status !== 'done')
                || results.find((x) => x.error && x.error.response?.status === 409);
            const failed = results.find((x) => x.error && x.error.response?.status !== 409);
            if (failed) {
                console.error('auto-approve failed', failed.error);
                setApproving(false);
                const serverMsg = failed.error?.response?.data?.msg;
                setError(serverMsg ? `Auto-approve failed: ${serverMsg}` : 'Auto-approve failed. Please try again.');
                return;
            }
            if (running) {
                pollApproveStatus(running.pid);
            } else {
                setApproving(false);
                fetchData();
            }
        } catch (err) {
            console.error('auto-approve failed', err);
            setApproving(false);
            setError('Auto-approve failed. Please try again.');
        }
    };

    const approveOne = (s) => {
        setApproved((a) => ({ ...a, [`${s.projectId}-${s.n}`]: true }));
        persistDecision(s, 'approved');
    };

    // Inline-edit a segment's translation without leaving the page. Uses the
    // propagation endpoint so every section that is currently identical (same
    // source + same target) is updated together — projects imported without
    // source dedup store identical segments separately, so a single edit would
    // otherwise miss the rest. Then patch all matching loaded rows. The QE score
    // stays stale until the next analysis run — we flag rows with `_edited`.
    const saveSegment = async (s, translatedText) => {
        await axios.put(`/api/projects/${s.projectId}/segment-identical`, {
            index: s.n - 1,
            translatedText,
            source: 'human',
        }, { headers: authHeader });
        setData((d) => ({
            ...d,
            segments: (d.segments || []).map((x) => (
                x.projectId === s.projectId && x.src === s.src && x.tgt === s.tgt
                    ? { ...x, tgt: translatedText, _edited: true }
                    : x
            )),
        }));
    };

    // Group sibling projects (same source file, different target languages) by
    // projectGroupId. The /api/projects?summary=true payload is already rolled
    // up — one row per source document with a `targetLanguages: [{ code,
    // projectId }]` array — so we expand each row back into one sibling per
    // target language. (A flat row without targetLanguages is treated as a
    // single-language group as a fallback.) Each group's `name` is taken from
    // any sibling (they share the base name) and siblings are sorted
    // alphabetically by target language for stable display.
    const projectGroups = useMemo(() => {
        const groups = new Map();
        projects.forEach((p) => {
            const gid = String(p.projectGroupId || p._id);
            if (!groups.has(gid)) {
                groups.set(gid, {
                    groupId: gid,
                    name: (p.projectName || p.name || 'Untitled project').replace(/\s*\([a-z-]{2,7}\)\s*$/i, ''),
                    sourceLanguage: p.sourceLanguage || 'en',
                    siblings: [],
                });
            }
            const langs = Array.isArray(p.targetLanguages) && p.targetLanguages.length
                ? p.targetLanguages
                : [{ projectId: p._id, code: p.targetLanguage }];
            langs.forEach((t) => {
                groups.get(gid).siblings.push({
                    _id: String(t.projectId),
                    targetLanguage: t.code,
                    sourceLanguage: p.sourceLanguage || 'en',
                    projectName: p.projectName || p.name,
                    projectGroupId: gid,
                });
            });
        });
        groups.forEach((g) => {
            g.siblings.sort((a, b) => String(a.targetLanguage || '').localeCompare(String(b.targetLanguage || '')));
        });
        return [...groups.values()];
    }, [projects]);

    const selectedGroup = useMemo(() => {
        if (scopeProjectId) {
            return projectGroups.find((g) => g.siblings.some((s) => s._id === scopeProjectId)) || null;
        }
        if (scopeGroupId) {
            return projectGroups.find((g) => g.groupId === scopeGroupId) || null;
        }
        return null;
    }, [projectGroups, scopeProjectId, scopeGroupId]);

    const activeGroupId = selectedGroup ? selectedGroup.groupId : '';

    const scopeLabel = useMemo(() => {
        if (scopeProjectId) {
            const p = selectedGroup?.siblings.find((x) => x._id === scopeProjectId);
            return p ? `${p.projectName || 'Untitled'} · ${(p.sourceLanguage || 'EN').toUpperCase()} → ${(p.targetLanguage || '').toUpperCase()}` : 'Project';
        }
        if (selectedGroup) {
            return `${selectedGroup.name} · all ${selectedGroup.siblings.length} languages`;
        }
        return 'All projects';
    }, [scopeProjectId, selectedGroup]);

    if (loading && !data.segments?.length) {
        return (
            <div style={{ padding: 40 }}>
                <div style={{ color: 'var(--ink-3)', marginBottom: 16 }}>Loading AI quality…</div>
                <div className="aiq-list">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="aiq-row" style={{ opacity: 0.45 }}>
                            <div style={{ width: 56, height: 56, borderRadius: 12, background: 'var(--bg-soft)' }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ height: 14, width: '60%', background: 'var(--bg-soft)', borderRadius: 4, marginBottom: 8 }} />
                                <div style={{ height: 12, width: '80%', background: 'var(--bg-soft)', borderRadius: 4 }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    if (error) {
        return (
            <div style={{ padding: 40 }}>
                <div style={{ color: 'crimson', marginBottom: 12 }}>{error}</div>
                <button type="button" className="set-btn" onClick={() => setReloadKey((k) => k + 1)}>
                    Try again
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">AI Quality</h1>
                <div className="rs-page-actions">
                    <select
                        className="set-input"
                        value={activeGroupId}
                        onChange={(e) => {
                            const gid = e.target.value;
                            setScopeProjectId('');
                            setScopeGroupId(gid);
                        }}
                        style={{ height: 32 }}
                    >
                        <option value="">All projects</option>
                        {projectGroups.map((g) => (
                            <option key={g.groupId} value={g.groupId}>{g.name}</option>
                        ))}
                    </select>
                    {selectedGroup && selectedGroup.siblings.length > 1 && (
                        <select
                            className="set-input"
                            value={scopeProjectId}
                            onChange={(e) => setScopeProjectId(e.target.value)}
                            style={{ height: 32 }}
                            title="Scope to one language, or leave on All languages to see the whole group"
                        >
                            <option value="">All {selectedGroup.siblings.length} languages</option>
                            {selectedGroup.siblings.map((s) => (
                                <option key={s._id} value={s._id}>
                                    {(s.sourceLanguage || 'EN').toUpperCase()} → {(s.targetLanguage || '').toUpperCase()}
                                </option>
                            ))}
                        </select>
                    )}
                    <span className="aiq-scope">{scopeLabel}</span>
                    <button
                        type="button"
                        className="btn-q"
                        onClick={() => runAnalysis({ force: true })}
                        disabled={analyzing}
                        title={
                            scopeProjectId
                                ? 'Re-score every segment with the QE model'
                                : selectedGroup
                                    ? `Re-score every segment across all ${selectedGroup.siblings.length} languages`
                                    : 'Pick a project to run the full QE pipeline'
                        }
                    >
                        {QI.refresh}
                        <span>
                            {analyzing
                                ? (progress > 0
                                    ? `Analyzing… ${progress}%${segTotal ? ` (≈${Math.round((progress / 100) * segTotal).toLocaleString()} / ${segTotal.toLocaleString()})` : ''}`
                                    : 'Analyzing…')
                                : 'Re-run analysis'}
                        </span>
                    </button>
                </div>
            </div>
            <p className="rs-page-sub">Rush AI scores every draft for adequacy, fluency, and risk before a human looks — so your team spends time only where it matters.</p>

            <div className="aiq-kpis">
                <div className="aiq-kpi"><div className="v">{avgScore}</div><div className="l">Avg quality estimate</div></div>
                <div className="aiq-kpi"><div className="v">{stats.totalSegs || 0}</div><div className="l">Segments analyzed</div></div>
                <div className="aiq-kpi good"><div className="v">{safeCount}</div><div className="l">Safe to auto-approve</div></div>
                <div className="aiq-kpi warn"><div className="v">{reviewCount + riskCount}</div><div className="l">Need your eyes</div></div>
            </div>

            <div className="rs-card aiq-triage">
                <div className="aiq-triage-h">
                    <div>
                        <h3>Triage</h3>
                        <span>Everything at or above your confidence bar with no high-severity risk is cleared for auto-approval.</span>
                    </div>
                    <button
                        type="button"
                        className="set-btn accent"
                        onClick={autoApprove}
                        disabled={approving || safeCount === 0 || (!scopeProjectId && !selectedGroup)}
                        style={(approving || safeCount === 0 || (!scopeProjectId && !selectedGroup)) ? { opacity: 0.5, cursor: 'not-allowed' } : null}
                        title={(!scopeProjectId && !selectedGroup) ? 'Pick a project or language to auto-approve' : `Approve all ${safeCount} safe segments`}
                    >
                        {QI.check} {approving
                            ? `Approving… ${approveProgress}%`
                            : `Auto-approve ${safeCount} safe`}
                    </button>
                </div>
                <div className="aiq-dist">
                    <div className="seg safe" style={{ width: `${pct(safeCount)}%` }} title="Safe" />
                    <div className="seg review" style={{ width: `${pct(reviewCount)}%` }} title="Review" />
                    <div className="seg risk" style={{ width: `${pct(riskCount)}%` }} title="Risk" />
                </div>
                <div className="aiq-legend">
                    <span><i className="d safe" />Safe · {safeCount}</span>
                    <span><i className="d review" />Review recommended · {reviewCount}</span>
                    <span><i className="d risk" />High risk · {riskCount}</span>
                </div>
                <div className="aiq-th">
                    <span className="lbl">Auto-approve at or above</span>
                    <input
                        type="range"
                        className="set-slider"
                        min={70}
                        max={100}
                        step={1}
                        value={threshold}
                        onChange={(e) => setThreshold(Number(e.target.value))}
                    />
                    <span className="val">{threshold}%</span>
                </div>
            </div>

            <div className="aiq-queue-h">
                <h3>Review queue</h3>
                <span className="ct">{reviewCount + riskCount}</span>
                <span className="sub">
                    {queue.length < (reviewCount + riskCount)
                        ? `riskiest ${queue.length} first`
                        : 'riskiest first'}
                </span>
            </div>
            <div className="aiq-list">
                {queue.length === 0 && (
                    <div className="aiq-empty">{QI.check} Queue clear — every segment is cleared or approved. Nice.</div>
                )}
                {queue.map((s) => {
                    const key = `${s.projectId}-${s.n}`;
                    return (
                        <SegmentRow
                            key={key}
                            s={s}
                            bucket={bucketOf(s, threshold)}
                            expanded={expanded === key}
                            onToggle={() => setExpanded(expanded === key ? null : key)}
                            onApprove={() => approveOne(s)}
                            onOpen={() => navigate(`/editor/${s.projectId}`)}
                            onSave={(text) => saveSegment(s, text)}
                            done={!!approved[key]}
                        />
                    );
                })}
            </div>
        </>
    );
};

export default AIQuality;
